/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import {
  DATAITEM_TYPE_DERIVED,
  DATAITEM_TYPE_DIMENSION,
  DATAITEM_TAG_DIMENSION,
  DATAITEM_TAG_ALERT,
  DATAITEM_TAG_EVENT,
  DATAITEM_TYPE_DERIVED_DIMENSION,
  DATAITEM_TYPE_EVENT,
  DATAITEM_TYPE_DERIVED_EVENT,
  DATAITEM_TYPE_DERIVED_ALERT,
  DATAITEM_TYPE_METRIC,
  DATAITEM_TYPE_CALCULATED_METRIC,
  KPI_DEFAULT_TIME_RANGE,
  INPUT_TYPE_DATA_ITEM,
  INPUT_TYPE_CONSTANT,
  INPUT_TYPE_DOMAIN,
  COLUMNTYPE_BOOLEAN,
  COLUMNTYPE_ARRAY,
  COLUMNTYPE_TIMESTAMP,
  DATAITEM_TAG_COMPLEX,
  KPI_CATEGORY_TRANSFORMER,
  KPI_CATEGORY_AGGREGATOR,
  KPI_CATEGORY_ALERT,
} from 'constants';

import { defineMessages } from 'react-intl';
import omit from 'lodash/omit';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import capitalize from 'lodash/capitalize';
import { getCardVariables, replaceVariables } from 'carbon-addons-iot-react';

import { Version } from '../../Common/types/CommonTypes';
import { COLUMNTYPE_LITERAL, COLUMNTYPE_NUMBER, RESOURCE_TYPES } from '../constants/constants';

const messages = defineMessages({
  outputMetricName: '{granularity}_{name}_{dataItem}',
  metric: 'Metric',
  calculatedMetric: 'Metric (calculated)',
  event: 'Event',
  calculatedEvent: 'Event (calculated)',
  calculatedAlert: 'Alert (calculated)',
  metricDerived: 'Metric (derived)',
  dimension: 'Dimension',
  calculatedDimension: 'Dimension (calculated)',
  calculatedBatchDataMetric: 'Batch data metric (calculated)',
  calculatedStreamingDataMetric: 'Streaming data metric (calculated)',

  // CalculateKPIInputField PII
  dataItems: 'Data Items',
  constants: 'Constants',
  missingDataItems: 'No data items found. Contact your system administrator.',
  missingConstants: 'No options found. Contact your system administrator.',
  selectConstant: 'Select constant',
  selectDataItem: 'Input data items',
  selectValue: 'Select value',
  colTypeString: 'String',
  colTypeBoolean: 'Boolean',
  colTypeNumber: 'Number',

  // data item tags
  alertTag: 'Alert',
  transformerTag: 'Transformer',
  aggregatorTag: 'Aggregator',
});

// Data Item Helpers
export function formatDataItemType(type, formatMessage) {
  switch (type) {
    case DATAITEM_TYPE_CALCULATED_METRIC:
      return formatMessage(messages.calculatedMetric);
    case DATAITEM_TYPE_EVENT:
      return formatMessage(messages.event);
    case DATAITEM_TYPE_DERIVED_EVENT:
      return formatMessage(messages.calculatedEvent);
    case DATAITEM_TYPE_DERIVED_ALERT:
      return formatMessage(messages.calculatedAlert);
    case DATAITEM_TYPE_DERIVED:
      return formatMessage(messages.metricDerived);
    case DATAITEM_TYPE_DIMENSION:
      return formatMessage(messages.dimension);
    case DATAITEM_TYPE_DERIVED_DIMENSION:
      return formatMessage(messages.calculatedDimension);
    case DATAITEM_TYPE_METRIC:
    default:
      return formatMessage(messages.metric);
  }
}

/** Used to format the V1 and V2 Devicetype/pipeline data item */
export function formatV2DataItem(type, formatMessage, version = Version.V1) {
  switch (type) {
    case DATAITEM_TYPE_CALCULATED_METRIC:
    case DATAITEM_TYPE_EVENT:
    case DATAITEM_TYPE_DERIVED_EVENT:
    case DATAITEM_TYPE_DERIVED_ALERT:
    case DATAITEM_TYPE_DERIVED:
      return version === Version.V1
        ? formatMessage(messages.calculatedBatchDataMetric)
        : formatMessage(messages.calculatedStreamingDataMetric);
    case DATAITEM_TYPE_DIMENSION:
      return formatMessage(messages.dimension);
    case DATAITEM_TYPE_DERIVED_DIMENSION:
      return formatMessage(messages.calculatedDimension);
    case DATAITEM_TYPE_METRIC:
    default:
      return formatMessage(messages.metric);
  }
}

export function determineDataItemTag(dataItem, formatMessage) {
  const catalogFunctionDto = dataItem?.kpiFunctionDto?.catalogFunctionDto;
  if (catalogFunctionDto) {
    switch (catalogFunctionDto.category) {
      case KPI_CATEGORY_TRANSFORMER:
        return formatMessage(messages.transformerTag);
      case KPI_CATEGORY_AGGREGATOR:
        return formatMessage(messages.aggregatorTag);
      case KPI_CATEGORY_ALERT:
        return formatMessage(messages.alertTag);
      default:
        return null;
    }
  }

  if (!Array.isArray(dataItem.tags)) {
    return null;
  }

  if (dataItem.tags.includes(DATAITEM_TAG_ALERT)) {
    return formatMessage(messages.alertTag);
  }

  return null;
}

export function isDataItemCalculated(dataItem) {
  switch (dataItem.type) {
    case DATAITEM_TYPE_CALCULATED_METRIC:
    case DATAITEM_TYPE_DERIVED_DIMENSION:
    case DATAITEM_TYPE_DERIVED_EVENT:
    case DATAITEM_TYPE_DERIVED_ALERT:
      return true;
    default:
      return false;
  }
}

/** is the data item a metric value, raw or calculated */
export function isDataItemMetric(dataItem) {
  switch (dataItem.type) {
    case DATAITEM_TYPE_CALCULATED_METRIC:
    case DATAITEM_TYPE_DERIVED:
    case DATAITEM_TYPE_METRIC:
      return true;
    default:
      return false;
  }
}

/** is the data item a metric value, raw or calculated */
export function isDataItemBoolean(dataItem) {
  return dataItem.columnType === COLUMNTYPE_BOOLEAN;
}

/** Give a human-readable column type for a data item */
export function formatDataItemColumnType(type, formatMessage) {
  switch (type) {
    case COLUMNTYPE_LITERAL:
      return formatMessage(messages.colTypeString);
    case COLUMNTYPE_BOOLEAN:
      return formatMessage(messages.colTypeBoolean);
    case COLUMNTYPE_NUMBER:
      return formatMessage(messages.colTypeNumber);
    case DATAITEM_TYPE_DERIVED_EVENT:
      return formatMessage(messages.calculatedEvent);
    default:
      return capitalize(type);
  }
}

export function isDataItemAlert(dataItem) {
  return (
    dataItem.type === DATAITEM_TYPE_DERIVED_EVENT ||
    dataItem.type === DATAITEM_TYPE_DERIVED_ALERT ||
    (dataItem?.tags?.includes(DATAITEM_TAG_ALERT) ?? false)
  );
}

export function determineDataItemType(dataItem) {
  if (!Array.isArray(dataItem?.tags)) {
    return dataItem.type;
  }
  if (dataItem.tags.includes(DATAITEM_TAG_DIMENSION)) {
    return DATAITEM_TYPE_DERIVED_DIMENSION; // Store tags as DERIVED_DIMENSION type since all the UI code is looking here
  }
  if (dataItem.tags.includes(DATAITEM_TAG_ALERT)) {
    return DATAITEM_TYPE_DERIVED_ALERT;
  }
  if (dataItem.tags.includes(DATAITEM_TAG_EVENT)) {
    return DATAITEM_TYPE_DERIVED_EVENT;
  }
  if (dataItem.tags.includes(DATAITEM_TAG_COMPLEX)) {
    return DATAITEM_TYPE_CALCULATED_METRIC;
  }
  return dataItem.type;
}

export function isDataItemRawMetric(dataItem) {
  const dataItemType = determineDataItemType(dataItem);
  return dataItemType === DATAITEM_TYPE_METRIC;
}

export function isDataItemDerived(dataItem) {
  return dataItem.type === DATAITEM_TYPE_DERIVED;
}

export function isDataItemDimension(dataItem) {
  return (
    dataItem.type === DATAITEM_TYPE_DIMENSION ||
    dataItem.type === DATAITEM_TYPE_DERIVED_DIMENSION ||
    dataItem.tags?.includes(DATAITEM_TAG_DIMENSION)
  );
}

export function isDataItemHasCatalogFunctionDtoProperty(dataItem) {
  return (
    dataItem.hasOwnProperty('kpiFunctionDto') &&
    dataItem.kpiFunctionDto.hasOwnProperty('catalogFunctionDto')
  );
}

function checkDataItemDerivedMetricType(dataItem, version = Version.V1) {
  return (
    isDataItemHasCatalogFunctionDtoProperty(dataItem) &&
    dataItem.kpiFunctionDto.catalogFunctionDto.version === version
  );
}

export function isDataItemStreamingMetric(dataItem) {
  return checkDataItemDerivedMetricType(dataItem, Version.V2);
}

export function isDataItemBatchMetric(dataItem) {
  return checkDataItemDerivedMetricType(dataItem, Version.V1);
}

function checkDataItemCategory(dataItem, category) {
  return (
    isDataItemHasCatalogFunctionDtoProperty(dataItem) &&
    dataItem.kpiFunctionDto.catalogFunctionDto.category === category
  );
}

export function isDataItemCategoryTransformer(dataItem) {
  return checkDataItemCategory(dataItem, KPI_CATEGORY_TRANSFORMER);
}

export function isDataItemCategoryAlert(dataItem) {
  return checkDataItemCategory(dataItem, KPI_CATEGORY_ALERT);
}

export function isDataItemCategoryAggreator(dataItem) {
  return checkDataItemCategory(dataItem, KPI_CATEGORY_AGGREGATOR);
}

/**
 * Display Aggregator Tab in the DataPane
 * if the resource type not DEVICE
 * AND
 * if streaming metric is enabled
 * AND
 * {
 * ( if raw data item and its columnType is not euqals to TIMESTAMP )
 * OR
 * (if streaming metric and of type TRANSFORMER)
 * }
 * @param {*} dataItem the data item on which the aggregator tab need to be made visible
 * @param {*} hasStreamingMetricEnabled to determin if the streaming pipeline enabled
 * @param {*} hierarchyItemType the type of resource. example: DEVICE, ASSET, ORGANIZATION ...
 * @returns
 */
export function showAggregatorTab(dataItem, hasStreamingMetricEnabled, hierarchyItemType) {
  return (
    hierarchyItemType !== RESOURCE_TYPES.DEVICE &&
    hasStreamingMetricEnabled &&
    ((isDataItemRawMetric(dataItem) && dataItem.columnType !== COLUMNTYPE_TIMESTAMP) ||
      (isDataItemStreamingMetric(dataItem) &&
        (isDataItemCategoryTransformer(dataItem) || isDataItemAlert(dataItem))))
  );
}

export function formatCatalogFunction(catalogFunction) {
  const inputMap = {};
  catalogFunction.input.forEach(input => {
    if (input.dataType === COLUMNTYPE_BOOLEAN) {
      inputMap[input.name] = false;
    } else {
      inputMap[input.name] = undefined;
    }
  });
  const outputMap = {};
  catalogFunction.output.forEach(output => {
    outputMap[output.name] = undefined;
  });
  return {
    input: inputMap,
    output: outputMap,
    functionName: catalogFunction.name,
    ...omit(catalogFunction, ['input', 'output', 'name']),
  };
}

/**
 *
 * @param {string} granularity
 * @param {object} kpi
 * @param {string} dataItem
 * @param {*} props
 */
export function generateOutputMetricName(granularity, kpi, dataItem, props) {
  const {
    intl: { formatMessage },
  } = props;
  return formatMessage(messages.outputMetricName, {
    granularity,
    name: kpi.functionName,
    dataItem,
  })
    .trim()
    .replace(/ /g, '_');
}

// function logic functions
export function doesDataItemSupportKPIs(dataItem) {
  const { type } = dataItem;
  return type === DATAITEM_TYPE_METRIC || type === DATAITEM_TYPE_EVENT;
}

export function determineTimeFrequency(dataItem) {
  return dataItem.kpiFunctionDto &&
    dataItem.kpiFunctionDto.granularity &&
    dataItem.kpiFunctionDto.granularity.frequency
    ? dataItem.kpiFunctionDto.granularity.frequency
    : KPI_DEFAULT_TIME_RANGE;
}

export function getOutputName(kpi) {
  return kpi && kpi.output ? kpi.output.name : kpi.functionName;
}

// Complex Function Utility Methods
export function isInputMultiSelect(fieldMetaData) {
  const { jsonSchema, type, dataType, values } = fieldMetaData;
  if (
    (type === INPUT_TYPE_CONSTANT && (!values || values.length === 0)) ||
    dataType !== COLUMNTYPE_ARRAY
  ) {
    // a constant type without values can't be a selection, must be typed
    return false;
  }
  if (jsonSchema) {
    const { maxItems, type: jsonType } = jsonSchema;
    return jsonType === 'array' && maxItems !== 1;
  }
  return true;
}

export function isInputTextField(fieldMetaData) {
  if (isInputMultiSelect(fieldMetaData)) {
    return false;
  }
  const { values } = fieldMetaData;
  return !!(
    (isNil(values) || isEmpty(values)) && // If we don't have values
    fieldMetaData.type === INPUT_TYPE_CONSTANT &&
    fieldMetaData.dataType !== COLUMNTYPE_BOOLEAN
  );
}

export function isInputDateTimePicker(inputField, matchingOptions) {
  const { type, dataType, dataTypeForArray } = inputField;
  return (
    type === INPUT_TYPE_CONSTANT &&
    matchingOptions.length === 0 && // no options in the picker
    (dataType === COLUMNTYPE_TIMESTAMP ||
      dataTypeForArray === COLUMNTYPE_TIMESTAMP ||
      (Array.isArray(dataTypeForArray) && dataTypeForArray.includes(COLUMNTYPE_TIMESTAMP)))
  );
}

export function isInputCheckbox(inputField) {
  return inputField.dataType === COLUMNTYPE_BOOLEAN;
}

export function sortAlphabetically(a, b) {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
}

/**
 * returns an array of valid selection choices for a given KPI Input
 * */
export function determineInputValues(
  fieldMetaData,
  dataItems,
  domains,
  version,
  hierarchyItemType
) {
  const { values, type, dataTypeForArray, dataType } = fieldMetaData;
  const inputDataType = dataType === COLUMNTYPE_ARRAY ? dataTypeForArray : dataType; // arrays put the type here

  // If they pass values, that's what we should show
  if (values) {
    return values;
  }
  switch (type) {
    case INPUT_TYPE_DATA_ITEM:
      return dataItems
        .filter(dataItem => {
          if (
            hierarchyItemType !== RESOURCE_TYPES.DEVICE_TYPE && // hierarchy level
            version === Version.V2 && // V2 KPI function
            isDataItemRawMetric(dataItem) // raw metric
          ) {
            return dataItem.version === version; // At hierarchy level V2 KPI function takes data from only V2 device type. V1 KPI function only takes data from V1 device type (raw metric + calculated metric) and V2 device type (raw metric).
          }

          if (isDataItemHasCatalogFunctionDtoProperty(dataItem)) {
            return dataItem.kpiFunctionDto.catalogFunctionDto.version === version; // V1 KPI function can not be used for V2 KPI function input and visa versa.
          }

          return true; // dimensional data items or raw metric for device type level
        })
        .filter(dataItem => !(version === Version.V2 && isDataItemDimension(dataItem))) // Hide dimensional dataItems for V2 device type configuration. Remove this once V2 streaming pipeline support dimensions.
        .sort(sortAlphabetically)
        .reduce((result, dataItem) => {
          if (
            (isNil(inputDataType) ||
              dataItem.columnType === inputDataType ||
              (Array.isArray(inputDataType) && inputDataType.includes(dataItem.columnType))) &&
            !result.includes(dataItem.name) // To remove the dublicate data item names even though backend sends more data. Issue: #4835
          ) {
            result.push(dataItem.name);
          }
          return result;
        }, []);
    // TODO: sort the values by their internal sequence
    case INPUT_TYPE_DOMAIN:
      return Array.isArray(domains) ? domains.map(domain => domain.value) : [];
    default:
      return [];
  }
}

export function determineInnerLabel(fieldMetaData, options, formatMessage) {
  const { type } = fieldMetaData;

  if (options && options.length === 0 && fieldMetaData.required) {
    return type === INPUT_TYPE_CONSTANT
      ? formatMessage(messages.missingConstants)
      : formatMessage(messages.missingDataItems);
  }
  if (isInputMultiSelect(fieldMetaData)) {
    return type === INPUT_TYPE_CONSTANT
      ? formatMessage(messages.constants)
      : formatMessage(messages.dataItems);
  }
  return type === INPUT_TYPE_CONSTANT
    ? formatMessage(messages.selectConstant)
    : type === INPUT_TYPE_DOMAIN
    ? formatMessage(messages.selectValue)
    : formatMessage(messages.selectDataItem);
}

/**
 * Determines the range of a particular key within an array of objects.
 * @param {array} data dataset to search within
 * @param {string} key a key present in the object to find the min/max of (does not support recursive/deeply nested objects)
 * @returns {string} a range
 */
export function getDataRangeByKey(data, key = 'x') {
  const min = data.length > 0 ? data[0] : undefined;
  const max = data.length > 0 ? data[data.length - 1] : undefined;
  let message = '';
  if (min) {
    message = min[key];
    if (max && max[key] !== min[key]) {
      message += ` - ${max[key]}`;
    }
  }
  return message;
}

export const isDataItemNumeric = dataItem => dataItem.columnType === COLUMNTYPE_NUMBER;

/**
 * Need to handle image cards on our side because the hotspot content is a user specified component that we
 * cannot re-assign at the addons level
 * @param {object} card
 * @returns {array} card object with all variables replaced with their corresponding values
 */
export const handleImageCardVariables = card => {
  const { cardVariables } = card;

  if (!cardVariables) {
    return card;
  }

  const imageCardVariables = getCardVariables(card);

  if (isEmpty(imageCardVariables)) {
    return card;
  }

  // These two special variables need to be excluded as we replace them later on
  return replaceVariables(
    difference(imageCardVariables, ['deviceid', 'entity_id']),
    cardVariables,
    card
  );
};

/**
 * // format dataItem name with prefix for duplicated name
 * @param {object} dataItem matched dataItem
 * @param {object} dataItemCounterByName includes dataItem name with repetitive count
 */
export const formatDataItemName = (dataItem, dataItemCounterByName) => {
  const { displayName, name, eventName } = dataItem;
  if (displayName) {
    return displayName;
  }
  if (eventName) {
    return dataItemCounterByName[name] > 1 ? `${eventName}.${name}` : name;
  }
  return name;
};
